import type { EventBinding } from "../api/events";
import type { EventStore } from "./event-operation-store";

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const KEY = /^evaluation:[a-z0-9]+:[0-9a-f]{36}$/;
export interface EvaluationOperation {
  eventId: string;
  revisionId: string;
  operationType:
    | "evaluation.create"
    | "evaluation.work.save"
    | "award.allocation.save"
    | "evaluation.complete";
  evaluationId?: string;
  awardId?: string;
  expectedVersion?: string;
  awardVersion?: string;
  idempotencyKey: string;
  startedAt: number;
}
export function evaluationBindingKey(binding: EventBinding) {
  if (
    binding.audience !== "workspace" ||
    !UUID.test(binding.tenantId) ||
    !UUID.test(binding.accountId)
  )
    throw new Error("Invalid evaluation binding");
  return `wio-veyra.evaluation-operations.v1:${binding.tenantId}:${binding.accountId}:`;
}
function project(value: unknown): EvaluationOperation {
  const row = value as Partial<EvaluationOperation> | null;
  if (
    !row ||
    !UUID.test(row.eventId ?? "") ||
    !UUID.test(row.revisionId ?? "") ||
    ![
      "evaluation.create",
      "evaluation.work.save",
      "award.allocation.save",
      "evaluation.complete",
    ].includes(row.operationType ?? "") ||
    !KEY.test(row.idempotencyKey ?? "") ||
    typeof row.startedAt !== "number" ||
    !Number.isFinite(row.startedAt) ||
    row.startedAt < 0
  )
    throw new Error("Invalid evaluation recovery identity");
  if (
    row.operationType !== "evaluation.create" &&
    (!UUID.test(row.evaluationId ?? "") ||
      !/^[1-9]\d{0,17}$/.test(row.expectedVersion ?? ""))
  )
    throw new Error("Missing evaluation version binding");
  if (row.awardId !== undefined && !UUID.test(row.awardId))
    throw new Error("Invalid award binding");
  if (
    row.operationType === "award.allocation.save" &&
    !/^(0|[1-9]\d{0,17})$/.test(row.awardVersion ?? "")
  )
    throw new Error("Missing allocation version binding");
  return {
    eventId: row.eventId!,
    revisionId: row.revisionId!,
    operationType: row.operationType!,
    idempotencyKey: row.idempotencyKey!,
    startedAt: row.startedAt,
    ...(row.operationType !== "evaluation.create"
      ? { evaluationId: row.evaluationId, expectedVersion: row.expectedVersion }
      : {}),
    ...(row.awardId ? { awardId: row.awardId } : {}),
    ...(row.operationType === "award.allocation.save"
      ? { awardVersion: row.awardVersion }
      : {}),
  };
}
function recordPrefix(
  binding: EventBinding,
  type: EvaluationOperation["operationType"],
) {
  const legacy = evaluationBindingKey(binding);
  return type === "evaluation.create" ? legacy : legacy.replace(".v1:", ".v2:");
}
export function restoreEvaluationOperations(
  store: EventStore,
  binding: EventBinding,
) {
  const prefix = evaluationBindingKey(binding),
    current = prefix.replace(".v1:", ".v2:"),
    records: EvaluationOperation[] = [];
  for (let index = 0; index < store.length; index++) {
    const key = store.key(index);
    if (!key?.startsWith(prefix) && !key?.startsWith(current)) continue;
    const row = project(JSON.parse(store.getItem(key) ?? "null"));
    if (key !== recordPrefix(binding, row.operationType) + row.idempotencyKey)
      throw new Error("Mismatched evaluation key");
    records.push(row);
  }
  if (records.length > 12)
    throw new Error("Evaluation recovery capacity exceeded");
  return records.sort((a, b) => a.startedAt - b.startedAt);
}
export function persistEvaluationOperation(
  store: EventStore,
  binding: EventBinding,
  value: EvaluationOperation,
) {
  const row = project(value),
    records = restoreEvaluationOperations(store, binding);
  if (
    records.length >= 12 &&
    !records.some((x) => x.idempotencyKey === row.idempotencyKey)
  )
    throw new Error("Evaluation recovery capacity exceeded");
  const key = recordPrefix(binding, row.operationType) + row.idempotencyKey,
    encoded = JSON.stringify(row);
  store.setItem(key, encoded);
  if (store.getItem(key) !== encoded)
    throw new Error("Evaluation recovery unavailable");
}
export function removeEvaluationOperation(
  store: EventStore,
  binding: EventBinding,
  key: string,
) {
  if (!KEY.test(key)) throw new Error("Invalid evaluation key");
  for (const prefix of [
    evaluationBindingKey(binding),
    recordPrefix(binding, "evaluation.work.save"),
  ]) {
    const storageKey = prefix + key;
    store.removeItem(storageKey);
    if (store.getItem(storageKey) !== null)
      throw new Error("Evaluation recovery unavailable");
  }
}
export function evaluationCompletion(
  value: unknown,
  operation: EvaluationOperation,
): string | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const field = (name: string) =>
    row[name] ?? row[name[0].toUpperCase() + name.slice(1)];
  const id = field("evaluationId");
  if (
    field("eventId") !== operation.eventId ||
    field("revisionId") !== operation.revisionId ||
    typeof id !== "string" ||
    !UUID.test(id)
  )
    return null;
  if (operation.operationType === "evaluation.create")
    return field("resourceId") === id &&
      String(field("version")) === "1" &&
      field("state") === "Working"
      ? id
      : null;
  if (
    id !== operation.evaluationId ||
    !operation.expectedVersion ||
    typeof field("resourceId") !== "string" ||
    !UUID.test(String(field("resourceId")))
  )
    return null;
  const next = String(BigInt(operation.expectedVersion) + BigInt(1));
  if (operation.operationType === "award.allocation.save") {
    const award = field("awardId");
    return typeof award === "string" &&
      UUID.test(award) &&
      field("resourceId") === award &&
      (!operation.awardId || award === operation.awardId) &&
      operation.awardVersion !== undefined &&
      String(field("version")) ===
        String(BigInt(operation.awardVersion) + BigInt(1)) &&
      String(field("evaluationVersion")) === next &&
      field("state") === "Draft"
      ? id
      : null;
  }
  return field("resourceId") !== id &&
    String(field("version")) === next &&
    field("state") ===
      (operation.operationType === "evaluation.complete"
        ? "Completed"
        : "Working")
    ? id
    : null;
}
